// Shared helper that maps a statConfig (from utils/statMapping.getStatConfigs)
// to a mobile column object matching the canonical accessor/render contract.
// (The former per-column totalCalculator is retired — totals come from the SSoT
// engine statTotals.computeTotals via MobileDynastyTable's totalsRow prop, which the
// accessor's isTotalsRow branch reads.)
//
// Bible § 14 (ratified 2026-05-12): per-league stat tables MUST derive
// their stat columns from league_settings.scoring_categories via
// getStatConfigs. This helper is the canonical translator from statConfig
// → mobile column object. All mobile column-factory files import from
// here; no factory may inline its own version.
//
// Contract:
//   config: { field, label, decimals, category, prefix? }
//   - `field` is dotted (e.g., 'pitching.whip')
//   - Totals row reads from row.totals[leafField]
//   - Rate stats (decimals >= 2 or prefix === '.') skip totals because
//     summing a rate is meaningless; counting stats sum.
//
// Optional opts (used by FA Search; team stats and roster pass nothing):
//   statsSlot: string — when set, the column key is built as
//              `stats.${statsSlot}.${config.field}` instead of the default
//              `stats.${config.field}`. Needed when the FA Search factory
//              wants its rolling-row sort key to match the active dateRange
//              slot (e.g. 'rolling_14_day').
//   getRollingStats: (player, category) => statsObj — overrides the default
//              `player.stats.rolling_14_day[category]` lookup for the
//              rolling row. FA Search uses getRollingStatsForWindow to
//              resolve the slot from dateRange.
//   styledRender: bool — when true, render() returns a styled <span> with
//              FA-Search rolling-row visual cues (italic yellow on rolling
//              row, white on season row). When false (default), render
//              returns the raw formatted value.

import React from 'react';
import { formatBattingAvg, formatERA } from '../services/statFormatUtils';

const formatValue = (config, value) => {
  if (value === null || value === undefined) return '-';
  if (config.decimals === 3 && config.prefix === '.') return formatBattingAvg(value);
  if (config.decimals === 2) return formatERA(value);
  if (config.decimals === 1) return (parseFloat(value) || 0).toFixed(1);
  return value;
};

export const buildDynamicStatColumn = (config, opts = {}) => {
  const parts = (config.field || '').split('.');
  const category = parts.length > 1 ? parts[0] : null;
  const leafField = parts.length > 1 ? parts.slice(1).join('.') : parts[0];

  const isRate = config.decimals >= 2 || config.prefix === '.';
  const colWidth = config.label && config.label.length >= 3 ? '34px' : '24px';
  const keyPrefix = opts.statsSlot ? `stats.${opts.statsSlot}.${category}.${leafField}` : `stats.${config.field}`;

  const accessor = (row) => {
    if (row.isTotalsRow) {
      return row.totals?.[leafField];
    }
    let stats;
    if (row.statType === 'season') {
      stats = category ? row.player?.stats?.season?.[category] : row.player?.stats?.season;
    } else if (row.statType === 'rolling') {
      // FA Search uses dateRange-aware rolling via opts.getRollingStats.
      // Team stats and roster default to the canonical rolling_14_day slot.
      if (opts.getRollingStats) {
        stats = opts.getRollingStats(row.player, category);
      } else {
        stats = category ? row.player?.stats?.rolling_14_day?.[category] : row.player?.stats?.rolling_14_day;
      }
    } else {
      stats = category ? row.player?.stats?.active_accrued?.[category] : row.player?.stats?.active_accrued;
    }
    return stats?.[leafField];
  };

  const render = opts.styledRender
    ? (value, row) => {
        const rowType = row?.statType;
        const color = rowType === 'season' ? '#fff' : '#fbbf24';
        const fontStyle = rowType === 'season' ? 'normal' : 'italic';
        const opacity = rowType === 'season' ? 1 : 0.6;
        return React.createElement(
          'span',
          { style: { color, fontSize: '10px', fontStyle, opacity, fontFamily: 'monospace' } },
          formatValue(config, value),
        );
      }
    : (value) => formatValue(config, value);

  // sortValue operates on RAW player objects (not wrapped {statType,player}
  // rows). MobileDynastyTable's sort comparator passes raw `data[i]`
  // directly; FA Search's data prop is an Array<Player>. The wrapped-row
  // `accessor` above returns undefined when called with a raw player,
  // which silently broke every mobile FA Search sort (Tony 2026-05-14
  // bug report). Tables that consume buildDynamicStatColumn with WRAPPED
  // rows (mobileRosterColumns, mobileTeamStatsColumns) do not set
  // `sortable: true` and never enter MobileDynastyTable's sort path —
  // so sortValue is unused for them.
  const sortStatsSlot = opts.statsSlot || 'season';
  const sortValue = (rawPlayer) => {
    if (!rawPlayer) return undefined;
    const stats = category
      ? rawPlayer.stats?.[sortStatsSlot]?.[category]
      : rawPlayer.stats?.[sortStatsSlot];
    return stats?.[leafField];
  };

  // DD-STAT-CONSOLIDATION C6: `totalCalculator` RETIRED. It was dead — MobileDynastyTable
  // renders its totals row from the `totalsRow` prop (computed by the SSoT engine
  // statTotals.computeTotals), never by invoking a per-column totalCalculator. It also
  // read a stale `stats.accrued` key. The SSoT engine is the sole totals authority.
  return {
    key: keyPrefix,
    header: config.label,
    title: config.label,  // FA Search reads .title, team stats reads .header — provide both
    width: colWidth,
    sortable: true,
    accessor,
    sortValue,
    render,
  };
};
